import { Check, Copy } from "lucide-react";
import { Button } from "@components/common/button";
import { useEffect, useState } from "react";
import axios from "axios";
import { DictType } from "@pma/types";
import useTranslator from "@pma/hooks/use-translator";
import { cn } from "@pma/utils/helpers";

interface Props {
  file: DictType;
}

export default function TextFileViewer({ file }: Props) {
  const [fileContent, setFileContent] = useState<string | null>(null);
  const [isCopied, setIsCopied] = useState(false);

  const { translate, locale } = useTranslator();

  const isHebrew = locale === "he";

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(file?.file, { responseType: "blob" });
        const content = await response.data.text();
        setFileContent(content);
      } catch (error) {
        console.error("Error fetching file:", error);
      }
    };

    fetchData();
  }, [file]);

  const copyToClipboard = async () => {
    if (fileContent) {
      try {
        await navigator.clipboard.writeText(fileContent);
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 2000);
      } catch (err) {
        console.error("Failed to copy text: ", err);
      }
    }
  };

  const isDoc =
    file?.file?.endsWith(".docx") || file?.file?.endsWith(".doc") || file?.file?.endsWith(".pptx");

  return (
    <div className={`relative w-full rounded-lg overflow-hidden pb-6 h-full`}>
      {isDoc ? (
        <>
          <iframe
            src={`https://view.officeapps.live.com/op/embed.aspx?src=${String(file?.file)}&embedded=true`}
            title={String(file?.name)}
            className='z-50 aspect-video h-full w-full overflow-y-auto object-contain'
            width='100%'
            height='500px'
          />
        </>
      ) : (
        <>
          {fileContent && (
            <div className='mt-4 space-y-2 h-full relative'>
              <div className={cn("absolute -top-4 z-20", isHebrew ? "left-1" : "right-1")}>
                <Button
                  onClick={copyToClipboard}
                  className='text-white  bg-secondary'
                  variant={isCopied ? "secondary" : "outline"}
                  disabled={isCopied}
                  size={"icon"}
                >
                  {isCopied ? (
                    <>
                      <Check className='h-4 w-4' />
                    </>
                  ) : (
                    <>
                      <Copy className='h-4 w-4' />
                    </>
                  )}
                </Button>
              </div>
              <pre className='bg-gray-800 max-w-fit h-full p-4 max-h-[75vh] min-w-full text-wrap rounded-md text-gray-300'>
                {fileContent}
              </pre>
            </div>
          )}
        </>
      )}
    </div>
  );
}
